import { DicomTagAS, DicomTagBase, DicomTagContents, DicomTagCS, DicomTagDA, DicomTagDS, DicomTagIS, DicomTagLO, DicomTagOB, DicomTagOW, DicomTagPN, DicomTagSH, DicomTagSQ, DicomTagTM, DicomTagUI, DicomTagUL, DicomTagUS } from "./dicom-vr";

export class DicomWriter {
	private static preamble?: Uint8Array;

	private data = new Map<number, Map<number, DicomTagContents>>();

	public instanceUID = DicomWriter.randomUID();

	// public storageClass = UID_PositronEmissionTomographyImageStorage;
	constructor(public storageClass: string) {

	}

	public save(): Blob {
		// https://dicom.nema.org/medical/dicom/current/output/chtml/part10/chapter_7.html
		if (!DicomWriter.preamble) {
			let preamble = DicomWriter.preamble = new Uint8Array(128 + 4);
			preamble.set([ 0x44, 0x49, 0x43, 0x4D ], 128); // "DICM"
		}

		// preamble, DICM, length of file meta section, file meta section, dicom data
		let fileMeta: DicomTagContents[] = [];
		fileMeta.push(DicomTags.FileMetaInformationVersion.make("\x00\x01"));
		fileMeta.push(DicomTags.MediaStorageSOPClassUID.make(this.storageClass));
		fileMeta.push(DicomTags.MediaStorageSOPInstanceUID.make(this.instanceUID));
		fileMeta.push(DicomTags.TransferSyntaxUID.make(UID_LittleEndianImplicit));
		fileMeta.push(DicomTags.ImplementationClassUID.make(UID_DedicaidDicomWriter));
		fileMeta.push(DicomTags.ImplementationVersionName.make("DicomWriter-1.0 "));
		
		let fileMetaBA = DicomTagContents.makeGroup(fileMeta, true, true);

		let blobContents = [ DicomWriter.preamble, ...fileMetaBA ];

		if (!this.hasTag(DicomTags.SOPInstanceUID))
			this.push(DicomTags.SOPInstanceUID.make(this.instanceUID));
		if (!this.hasTag(DicomTags.SOPClassUID))
			this.push(DicomTags.SOPClassUID.make(this.storageClass));
		if (!this.hasTag(DicomTags.SpecificCharacterSet))
			this.push(DicomTags.SpecificCharacterSet.make("ISO_IR 100"));

		let groups = Array.from(this.data.keys()).sort((a, b) => a - b);

		groups.forEach(groupId => {
			let tags = Array.from(this.data.get(groupId).values());
			blobContents.push(...DicomTagContents.makeGroup(tags, false));
		});

		return new Blob(blobContents);
	}

	public static calculateGroupLength(values: ArrayBuffer[]) {
		return values.reduce((acc, cur) => acc + cur.byteLength, 0);
	}

	public push(...tags: DicomTagContents[]) {
		tags.forEach(tag => {
			if (!this.data.has(tag.groupNum))
				this.data.set(tag.groupNum, new Map([[tag.elementNum, tag]]));
			else
				this.data.get(tag.groupNum).set(tag.elementNum, tag);
		});
		return this;
	}

	public static randomUID() {
		let result = "1.2.752.37.54.1745.";

		for (let i = 0; i < 40; i++)
			result += getRandomInt(0, 9).toFixed(0);

		return result;
	}

	public hasTag(what: DicomTagBase) {
		if (!this.data.has(what.groupNum))
			return false;
		return this.data.get(what.groupNum).has(what.elementNum);
	}
}

function getRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export namespace DicomTags {
	export const FileMetaInformationVersion = new DicomTagOB(0x0002, 0x0001);
	export const MediaStorageSOPClassUID = new DicomTagUI(0x0002, 0x0002);
	export const MediaStorageSOPInstanceUID = new DicomTagUI(0x0002, 0x0003);
	export const TransferSyntaxUID = new DicomTagUI(0x0002, 0x0010);
	export const ImplementationClassUID = new DicomTagUI(0x0002, 0x0012);
	export const ImplementationVersionName = new DicomTagSH(0x0002, 0x0013);

	export const ImageType = new DicomTagCS(0x0008, 0x0008);
	export const SpecificCharacterSet = new DicomTagCS(0x0008, 0x0005);
	export const InstanceCreationDate = new DicomTagDA(0x0008, 0x0012);
	export const InstanceCreationTime = new DicomTagTM(0x0008, 0x0013);
	export const SOPInstanceUID = new DicomTagUI(0x0008, 0x0018);
	export const SOPClassUID = new DicomTagUI(0x0008, 0x0016);
	export const StudyDate = new DicomTagDA(0x0008, 0x0020);
	export const SeriesDate = new DicomTagDA(0x0008, 0x0021);
	export const AcquisitionDate = new DicomTagDA(0x0008, 0x0022);
	export const ContentDate = new DicomTagDA(0x0008, 0x0023);
	export const StudyTime = new DicomTagTM(0x0008, 0x0030);
	export const SeriesTime = new DicomTagTM(0x0008, 0x0031);
	export const AcquisitionTime = new DicomTagTM(0x0008, 0x0032);

	export const Modality = new DicomTagCS(0x0008, 0x0060);
	export const StudyDescription = new DicomTagLO(0x0008, 0x1030);
	export const SeriesDescription = new DicomTagLO(0x0008, 0x103e);

	export const StudyInstanceUID = new DicomTagUI(0x0020, 0x000d);
	export const SeriesInstanceUID = new DicomTagUI(0x0020, 0x000e);

	export const StudyID = new DicomTagSH(0x0020, 0x0010);
	export const PatientID = new DicomTagLO(0x0010, 0x0020);
	export const IssuerOfPatientID = new DicomTagLO(0x0010, 0x0021);
	export const PatientName = new DicomTagPN(0x0010, 0x0010);
	export const OtherPatientIDsSequence = new DicomTagSQ(0x0010, 0x1002);
	export const PatientAge = new DicomTagAS(0x0010, 0x1010);
	export const PatientWeight = new DicomTagDS(0x0010, 0x1030);
	export const PatientBirthDate = new DicomTagDA(0x0010, 0x0030);
	export const FrameOfReferenceUID = new DicomTagUI(0x0020, 0x0052);

	export const RadiopharmaceuticalInformationSequence = new DicomTagSQ(0x0054, 0x0016);
	export const Radiopharmaceutical = new DicomTagLO(0x0018, 0x0031);
	export const RadionuclideTotalDose = new DicomTagDS(0x0018, 0x1074);
	export const RadionuclideCodeSequence = new DicomTagSQ(0x0054, 0x0300);
	export const RadiopharmaceuticalCodeSequence = new DicomTagSQ(0x0054, 0x0304);
	export const CodeValue = new DicomTagSH(0x0008, 0x0100);
	export const CodeMeaning = new DicomTagLO(0x0008, 0x0104);
	export const CodingSchemeDesignator = new DicomTagSH(0x0008, 0x0102);

	export const SeriesType = new DicomTagCS(0x0054, 0x1000);
	export const Units = new DicomTagCS(0x0054, 0x1001);
	export const PixelData = new DicomTagOW(0x7fe0, 0x0010);

	// MRI stuff
	export const RepetitionTime = new DicomTagDS(0x0018, 0x0080);
	export const EchoTime = new DicomTagDS(0x0018, 0x0081);
	export const MRDiffusionSequence = new DicomTagSQ(0x0018, 0x9117);

	export const DeidentificationMethodSequence = new DicomTagSQ(0x0012, 0x0064);
	export const DeidentificationMethod = new DicomTagLO(0x0012, 0x0063);
	export const PatientIdentityRemoved = new DicomTagCS(0x0012, 0x0062);

	// SEG stuff
	export const SegmentSequence = new DicomTagSQ(0x0062, 0x0002);
	export const SegmentLabel = new DicomTagLO(0x0062, 0x0005);
	export const SegmentNumber = new DicomTagUL(0x0062, 0x0004);
	export const SegmentDescription = new DicomTagLO(0x0062, 0x0006);
	export const SegmentAlgorithmType = new DicomTagCS(0x0062, 0x0008);
	export const SegmentAlgorithmName = new DicomTagLO(0x0062, 0x0009);
	export const RecommendedDisplayCIELabValue = new DicomTagUS(0x0062, 0x000D);

	export const SegmentedPropertyCategoryCodeSequence = new DicomTagSQ(0x0062, 0x0003);
	export const SegmentedPropertyTypeCodeSequence = new DicomTagSQ(0x0062, 0x000F);

	export const SegmentationType = new DicomTagCS(0x0062, 0x0001);
	export const MaximumFractionalValue = new DicomTagUS(0x0062, 0x000E);
	export const SegmentationFractionalType = new DicomTagCS(0x0062, 0x0010);

	// Generic tags

	export const Manufacturer = new DicomTagLO(0x0008, 0x0070);
	export const SeriesNumber = new DicomTagIS(0x0020, 0x0011);

	// RTSTRUCT tags
	export const ReferencedFrameOfReferenceSequence = new DicomTagSQ(0x3006, 0x0010);
	export const StructureSetLabel = new DicomTagSH(0x3006, 0x0002);
	export const StructureSetName = new DicomTagLO(0x3006, 0x0004);
	export const StructureSetDate = new DicomTagDA(0x3006, 0x0008);
	export const StructureSetTime = new DicomTagTM(0x3006, 0x0009);

	export const OperatorsName = new DicomTagPN(0x0008, 0x1070);
	export const AccessionNumber = new DicomTagSH(0x0008, 0x0050);
	export const ReferringPhysicianName = new DicomTagPN(0x0008, 0x0090);

	export const ReferencedSeriesSequence = new DicomTagSQ(0x0008, 0x1115);
	export const ReferencedInstanceSequence = new DicomTagSQ(0x0008, 0x114A);
	export const ReferencedSOPClassUID = new DicomTagUI(0x0008, 0x1150);
	export const ReferencedSOPInstanceUID = new DicomTagUI(0x0008, 0x1155);
	export const RTReferencedStudySequence = new DicomTagSQ(0x3006, 0x0012);
	export const RTReferencedSeriesSequence = new DicomTagSQ(0x3006, 0x0014);
	export const ContourImageSequence = new DicomTagSQ(0x3006, 0x0016);

	export const PerformedProcedureStepID = new DicomTagSH(0x0040, 0x0253);
	export const SoftwareVersions = new DicomTagLO(0x0018, 0x1020);
}

// https://dicom.nema.org/dicom/2013/output/chtml/part04/sect_i.4.html
export namespace DicomStorageClass {
	export const PositronEmissionTomographyImageStorage = "1.2.840.10008.5.1.4.1.1.128";
	export const RTStructureSetStorage = "1.2.840.10008.5.1.4.1.1.481.3";
	export const CTImageStorage = "1.2.840.10008.5.1.4.1.1.2";
	export const MRImageStorage = "1.2.840.10008.5.1.4.1.1.4";
	export const EncapsulatedPDFStorage = "1.2.840.10008.5.1.4.1.1.104.1";
}

export const UID_LittleEndianImplicit = "1.2.840.10008.1.2";

const UID_DedicaidDicomWriter = "1.2.555.741.1.1.1";

